import { useCallback, useEffect, useRef, useState } from "react";
import { API, deleteCommande } from "../../../services/api";
import { connect, disconnect, subscribe } from "../../../services/lockSocket";
import { DELETE_UNDO_SECONDS } from "../model/commandeConstants";
import { adaptCommandeStatusToContext, keepReleasedForContext } from "../model/commandeDraft";

// Aperçu de la commande cliquée dans la liste, relu sans cache ; masqué sur mobile où le clic ouvre le détail.
export function useCommandePreview({ view, isMobile, context }) {
    const [previewCommande, setPreviewCommande] = useState(null);
    const [previewDetail, setPreviewDetail] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewRevision, setPreviewRevision] = useState(0);

    useEffect(() => {
        if (isMobile) setPreviewCommande(null);
    }, [isMobile]);

    useEffect(() => {
        if (!previewCommande?.id || view !== "list") {
            setPreviewDetail(null);
            setPreviewLoading(false);
            return undefined;
        }

        const requestedId = previewCommande.id;
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewDetail(null);

        API.get(`/commandes/${requestedId}`, {
            params: { _ts: Date.now() },
            headers: {
                "Cache-Control": "no-cache, no-store",
                Pragma: "no-cache",
            },
        })
            .then(response => {
                if (!cancelled && response.data?.id === requestedId) {
                    setPreviewDetail(keepReleasedForContext(adaptCommandeStatusToContext(response.data, context), context));
                }
            })
            .catch(error => {
                if (!cancelled) console.error(error);
            })
            .finally(() => {
                if (!cancelled) setPreviewLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [previewCommande?.id, previewRevision, view, context]);

    const reloadPreview = useCallback(() => setPreviewRevision(revision => revision + 1), []);
    return { previewCommande, setPreviewCommande, previewDetail, previewLoading, reloadPreview };
}

// Suppression d'un mail depuis le détail : confirmation, puis 15 s pour annuler avant l'appel au serveur. Les
// suppressions en attente partent tout de suite quand l'utilisateur ouvre une autre commande ou quitte la page.
export function useDetailDeletion({ goToList, setCommandes, refresh }) {
    const [deleteConfirmationCommand, setDeleteConfirmationCommand] = useState(null);
    const [pendingDeleteDetails, setPendingDeleteDetails] = useState([]);
    const pendingDeleteDetailsRef = useRef([]);
    const deleteDetailTimersRef = useRef(new Map());
    const deleteDetailIntervalsRef = useRef(new Map());
    const deleteCancelButtonRef = useRef(null);

    useEffect(() => {
        pendingDeleteDetailsRef.current = pendingDeleteDetails;
    }, [pendingDeleteDetails]);

    const stopTimers = id => {
        clearTimeout(deleteDetailTimersRef.current.get(id));
        clearInterval(deleteDetailIntervalsRef.current.get(id));
    };

    const flushPendingDetailDeletes = useCallback((refreshAfter = false) => {
        const pending = pendingDeleteDetailsRef.current;
        if (pending.length === 0) return;
        pending.forEach(item => {
            stopTimers(item.cmd.id);
            Promise.resolve(deleteCommande(item.cmd.id)).catch(() => {});
        });
        deleteDetailTimersRef.current.clear();
        deleteDetailIntervalsRef.current.clear();
        pendingDeleteDetailsRef.current = [];
        setPendingDeleteDetails([]);
        if (refreshAfter) refresh();
    }, [refresh]);

    const handleDeleteFromDetail = useCallback((cmd) => {
        goToList();
        setCommandes(prev => prev.filter(c => c.id !== cmd.id));
        if (pendingDeleteDetailsRef.current.some(item => item.cmd.id === cmd.id)) return;

        setPendingDeleteDetails(prev => [...prev, { cmd, secondsLeft: DELETE_UNDO_SECONDS }]);

        const interval = setInterval(() => {
            setPendingDeleteDetails(prev => prev.map(item =>
                item.cmd.id === cmd.id
                    ? { ...item, secondsLeft: Math.max(0, item.secondsLeft - 1) }
                    : item
            ));
        }, 1000);
        deleteDetailIntervalsRef.current.set(cmd.id, interval);

        const timer = setTimeout(async () => {
            clearInterval(deleteDetailIntervalsRef.current.get(cmd.id));
            deleteDetailTimersRef.current.delete(cmd.id);
            deleteDetailIntervalsRef.current.delete(cmd.id);
            try { await deleteCommande(cmd.id); } catch { /* ignore */ }
            setPendingDeleteDetails(prev => prev.filter(item => item.cmd.id !== cmd.id));
        }, DELETE_UNDO_SECONDS * 1000);
        deleteDetailTimersRef.current.set(cmd.id, timer);
    }, [goToList, setCommandes]);

    const confirmDeleteFromDetail = useCallback(() => {
        if (!deleteConfirmationCommand) return;
        const commandToDelete = deleteConfirmationCommand;
        setDeleteConfirmationCommand(null);
        handleDeleteFromDetail(commandToDelete);
    }, [deleteConfirmationCommand, handleDeleteFromDetail]);

    useEffect(() => {
        if (!deleteConfirmationCommand) return undefined;

        deleteCancelButtonRef.current?.focus();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setDeleteConfirmationCommand(null);
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [deleteConfirmationCommand]);

    const handleCancelDeleteDetail = useCallback((id) => {
        stopTimers(id);
        deleteDetailTimersRef.current.delete(id);
        deleteDetailIntervalsRef.current.delete(id);
        setPendingDeleteDetails(prev => prev.filter(item => item.cmd.id !== id));
        refresh();
    }, [refresh]);

    useEffect(() => {
        const deleteDetailTimers = deleteDetailTimersRef.current;
        const deleteDetailIntervals = deleteDetailIntervalsRef.current;
        return () => {
            pendingDeleteDetailsRef.current.forEach(item => {
                clearTimeout(deleteDetailTimers.get(item.cmd.id));
                clearInterval(deleteDetailIntervals.get(item.cmd.id));
                Promise.resolve(deleteCommande(item.cmd.id)).catch(() => {});
            });
            pendingDeleteDetailsRef.current = [];
            deleteDetailTimers.clear();
            deleteDetailIntervals.clear();
        };
    }, []);

    return {
        deleteConfirmationCommand, setDeleteConfirmationCommand, pendingDeleteDetails, deleteCancelButtonRef,
        flushPendingDetailDeletes, confirmDeleteFromDetail, handleCancelDeleteDetail,
    };
}

// Événements temps réel de la pharmacie : verrous pris et relâchés, commandes modifiées ailleurs.
export function useLockEvents({ currentPharmacyId, list, lock }) {
    const { refresh, setCommandes, applyListPatches } = list;
    const { selectedCommandeRef, setLockedBy, hasLockRef, tryAcquire } = lock;
    useEffect(() => {
        if (!currentPharmacyId) return;
        connect(currentPharmacyId);
        const unsubscribe = subscribe((event) => {
            const { type, commandeId, lockedBy: lockHolder } = event;
            if (type === "COMMANDE_UPDATED") {
                refresh();
                return;
            }
            if (type !== "COMMANDE_LOCKED" && type !== "COMMANDE_UNLOCKED") return;
            const id = Number(commandeId);
            setCommandes(prev => applyListPatches(prev.map(c =>
                c.id === id ? { ...c, lockedBy: type === "COMMANDE_UNLOCKED" ? null : lockHolder } : c
            )));
            // Mise à jour du détail courant (via ref pour éviter la stale closure)
            if (selectedCommandeRef.current?.id === id) {
                if (type === "COMMANDE_UNLOCKED") {
                    setLockedBy(null);
                    if (!hasLockRef.current) tryAcquire(id);
                } else if (type === "COMMANDE_LOCKED") {
                    setLockedBy(lockHolder);
                }
            }
            // La libération suit normalement la dernière sauvegarde. Relire
            // immédiatement évite d'attendre le polling périodique.
            if (type === "COMMANDE_UNLOCKED") refresh();
        });
        return () => {
            if (typeof unsubscribe === "function") unsubscribe();
            disconnect();
        };
    // Reconnexion seulement si la pharmacie ou la liste change, comme avant le découpage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPharmacyId, refresh]);
}
